import { PartnerSettingValue } from '@provision/entity-types';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import type {
  AllowedRuleSet,
  MatchType,
  PartnerGroupRules,
  UserGroupRules,
} from 'src/graphql/entities/group-rules/group-rules.types';
import { isSettingsEnabled } from 'src/graphql/entities/partner-setting';
import { type NavigationDestination } from 'src/utils/navigation/navigation.types';
import {
  NavbarItemId,
  PLATFORM_PARTNER_HIDDEN_MENU_ITEMS,
} from 'src/utils/navigation/workspace-item-ids';

const isPlatformPartnerViewsItem = (
  itemId: string,
  selectedPartnerId: number | null,
): boolean =>
  selectedPartnerId === PLATFORM_PARTNER_ID && itemId === (NavbarItemId.Views as string);

export const filterItemsByAccess = <Item extends NavigationDestination>(data: {
  items: Item[];
  checkValidity: (
    rules: (PartnerGroupRules | UserGroupRules | AllowedRuleSet)[],
    opts: { matchType: MatchType },
  ) => boolean;
  isWorkingWithPartner: boolean;
  isMobile: boolean;
  partnerSettings: Partial<PartnerSettingValue>[];
  selectedPartnerId: number | null;
}): Item[] => {
  const {
    items,
    checkValidity,
    isWorkingWithPartner,
    isMobile,
    partnerSettings,
    selectedPartnerId,
  } = data;

  return items.filter((item) => {
    if (
      selectedPartnerId === PLATFORM_PARTNER_ID &&
      PLATFORM_PARTNER_HIDDEN_MENU_ITEMS.includes(item.id)
    ) {
      return false;
    }

    let available = true;

    if (
      item.allowedRules !== undefined &&
      !isPlatformPartnerViewsItem(item.id, selectedPartnerId)
    ) {
      const matchType: MatchType = item.matchType ?? 'any';
      available = checkValidity(item.allowedRules, { matchType });
    }

    if (available && item.disallowedRules !== undefined) {
      const matchType: MatchType = item.matchType ?? 'any';
      const isDisallowed = checkValidity(item.disallowedRules, { matchType });
      available = !isDisallowed;
    }

    if (available && item.disallowedPartnerSettings?.length) {
      const settingsDisallowMatch =
        item.disallowedPartnerSettings.some((settingId) =>
          partnerSettings.some((data) =>
            isSettingsEnabled(settingId, data as PartnerSettingValue),
          ),
        ) ?? false;

      return !settingsDisallowMatch;
    }

    if (!isWorkingWithPartner && available && 'isMultitenancyAllowed' in item) {
      available = Boolean(item.isMultitenancyAllowed);
    }

    if (isMobile && available && 'isMobileSupported' in item) {
      available = Boolean(item.isMobileSupported);
    }
    return available;
  });
};
